import type { AdminDashboardSectionCardProps } from "../../models/admin-dashboard.models";
import { adminStyles } from "../../styles/admin.styles";
import { ADMIN_DASHBOARD_TONE_CLASSES } from "../../utils/admin-dashboard.constants";
import AdminDashboardBreakdown from "./AdminDashboardBreakdown";
import AdminDashboardJobTrend from "./AdminDashboardJobTrend";
import { ArrowUpRightIcon } from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";
import { twMerge } from "tailwind-merge";

// Keep each section small: two signals, one chart, and a direct route to the full working page.
const AdminDashboardSectionCard = ({ section, result, path }: AdminDashboardSectionCardProps) => {
  const overview = result?.status === "ready" ? result.overview : undefined;
  const isLoading = !result;

  return (
    <section
      data-test={`admin-dashboard-section-${section.id}`}
      aria-labelledby={`admin-dashboard-title-${section.id}`}
      aria-busy={isLoading}
      className={twMerge(adminStyles.card.surface, "min-w-0 p-5", section.id === "cronJobs" && "md:col-span-2")}
    >
      <h3 id={`admin-dashboard-title-${section.id}`} className="mb-5 text-sm font-semibold">
        <Link
          to={path}
          data-test={`admin-dashboard-open-${section.id}`}
          className="flex items-center justify-between gap-3 rounded-sm hover:text-green focus-visible:outline focus-visible:outline-2 focus-visible:outline-green"
        >
          {section.title}
          <ArrowUpRightIcon className="h-4 w-4 opacity-50" aria-hidden="true" />
        </Link>
      </h3>
      {isLoading && (
        <div className="min-h-[148px] motion-safe:animate-pulse" data-test={`admin-dashboard-loading-${section.id}`}>
          <span className="sr-only">Loading {section.title}</span>
          <div aria-hidden="true" className="mb-6 grid grid-cols-2 gap-6">
            <div className="h-12 bg-[#E9F4F3] dark:bg-gray-700" />
            <div className="h-12 bg-[#E9F4F3] dark:bg-gray-700" />
          </div>
          <div aria-hidden="true" className="h-14 bg-[#E9F4F3] dark:bg-gray-700" />
        </div>
      )}
      {result?.status === "error" && (
        <div role="status" className="min-h-[148px] py-5" data-test={`admin-dashboard-unavailable-${section.id}`}>
          <p className="text-sm font-medium">Overview unavailable</p>
          <p className="mt-2 text-xs opacity-60">Refresh to try again, or open this section.</p>
        </div>
      )}
      {overview && (
        <>
          <dl className="mb-6 grid grid-cols-2 gap-5">
            {overview.metrics.map((metric) => (
              <div
                key={metric.key}
                className="min-w-0"
                data-test={`admin-dashboard-metric-${section.id}-${metric.key}`}
              >
                <dt className="mb-2 text-xs opacity-60">{metric.label}</dt>
                <dd
                  className={twMerge(
                    "break-words text-2xl font-semibold tabular-nums leading-tight",
                    ADMIN_DASHBOARD_TONE_CLASSES[metric.tone ?? "neutral"],
                  )}
                >
                  {typeof metric.value === "number" ? metric.value.toLocaleString() : metric.value}
                </dd>
              </div>
            ))}
          </dl>
          {overview.dailyCounts && <AdminDashboardJobTrend dailyCounts={overview.dailyCounts} />}
          {overview.breakdown && <AdminDashboardBreakdown breakdown={overview.breakdown} />}
          {overview.note && <p className="text-xs leading-5 opacity-60">{overview.note}</p>}
        </>
      )}
    </section>
  );
};

export default AdminDashboardSectionCard;
